import CircularProgress from '@mui/material/CircularProgress'
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'

import useSharedStyles from 'app/shared-components/BatchActions/BatchActions.shared.styles'
import DomainObjectForm from 'app/shared-components/DomainObjectForm'
import {
    FormPart,
} from 'app/types/form.types'
import RichTable from 'app/shared-components/RichTableReduxWrapper'
import Button from 'app/shared-components/Button'
import HistoryBlockPrompt from 'app/shared-components/HistoryBlockPrompt'
import useBeforeUnloadDialog from 'app/hooks/useBeforeUnloadDialog'
import {
    RichTableColumns,
} from 'app/types/richtable.types'
import WarningCard from 'app/shared-components/WarningCard'
import {
    WarningCardVariant,
} from 'app/enums/common.enums'
import SkyNetPagination from 'app/shared-components/SkyNetPagination'

import useLabel from 'app/hooks/useLabel'
import useStyles from './UploadFeedback.styles'
import usePaginatedData from './hooks/usePaginatedData'
import {
    FailedUpload,
    FeedbackFormData,
} from './UploadFeedback.types'

type Props = {
    failedUploads: FailedUpload[],
    isLoading: boolean
    uniqField: string
    feedbackFormValue: FeedbackFormData
    formFields: FormPart<keyof FeedbackFormData>[]
    onStopUpdate: () => void
    onDoneClick: () => void
    columns: RichTableColumns<any>[]
    rowsPerPage?: number
    renderCustomFinalMessage?: (data: { isStoppedByUser: boolean }) => JSX.Element
}

const UploadFeedback = ({
    failedUploads,
    isLoading,
    uniqField,
    formFields,
    feedbackFormValue,
    onStopUpdate,
    onDoneClick,
    columns,
    rowsPerPage = 25,
    renderCustomFinalMessage,
}: Readonly<Props>) => {
    const l = useLabel()
    const {
        classes,
    } = useStyles()
    const {
        classes: sharedClasses,
    } = useSharedStyles()

    useBeforeUnloadDialog(isLoading)

    const [
        stopped,
        setStopped,
    ] = useState({
        isStopped: false,
        stoppedManually: false,
    })

    useEffect(() => {
        if (!isLoading && !stopped.isStopped) {
            setStopped({
                stoppedManually: false,
                isStopped: true,
            })
        }
    }, [
        isLoading,
        stopped.isStopped,
    ])

    const {
        currentPageData,
        totalPages,
        currentPage,
        setCurrentPage,
        totalRecords,
    } = usePaginatedData<FailedUpload>({
        data: failedUploads,
        rowsPerPage,
    })

    const load = useCallback(() => {
        return Promise.resolve({
            data: currentPageData,
        })
    }, [currentPageData])

    const handleStopUpdate = useCallback(() => {
        setStopped({
            isStopped: true,
            stoppedManually: true,
        })
        onStopUpdate()
    }, [onStopUpdate])

    const FinalMessage = useMemo(() => {
        const isStoppedByUser = stopped.isStopped && stopped.stoppedManually

        if (renderCustomFinalMessage) {
            return renderCustomFinalMessage({
                isStoppedByUser,
            })
        }

        if (isStoppedByUser) {
            return (
                <WarningCard variant={WarningCardVariant.Warning}>
                    <span className={classes.importantText}>
                        {l('IMPORTANT')}
                        :
                    </span>
                    {l('UPDATE_STOPPED_BY_USER')}
                </WarningCard>
            )
        }
        return (
            <div>
                <WarningCard variant={WarningCardVariant.Success}>
                    <span className={classes.importantText}>
                        {l('IMPORTANT')}
                        :
                    </span>
                    {l('UPDATE_SUCCESSFULLY_COMPLETED')}
                </WarningCard>
                <div className={classes.textMessage}>
                    {l('ALL_RECORDS_WERE_PROCESSED')}
                </div>
            </div>

        )
    }, [
        classes.importantText,
        classes.textMessage,
        l,
        renderCustomFinalMessage,
        stopped.isStopped,
        stopped.stoppedManually,
    ])

    return (
        <>
            <div className={classes.container}>
                {isLoading ? (
                    <div className={sharedClasses.loadingContainer}>
                        <CircularProgress />
                        {l('UPDATE_IN_PROGRESS')}
                    </div>
                ) : FinalMessage}

                <DomainObjectForm
                    value={feedbackFormValue}
                    fields={formFields}
                    wrapper="noCard"
                    classNames={{
                        gridCardWrapper: classes.gridCardWrapper,
                    }}
                />

                {failedUploads.length > 0 && (
                    <>
                        <RichTable
                            uniqField={uniqField}
                            configName="UploadFeedbackTable"
                            name="UploadFeedback"
                            load={load}
                            columns={columns}
                            className={classes.tableContainer}
                            classNames={{
                                contentWrapper: classes.tableContentWrapper,
                            }}
                        />
                        {totalPages > 1 && (
                            <div className={classes.paginationContainer}>
                                <SkyNetPagination
                                    page={currentPage}
                                    total={totalRecords}
                                    rowsPerPage={rowsPerPage}
                                    onChange={(_, page) => {
                                        setCurrentPage(page)
                                    }}
                                    useCard={false}
                                />
                            </div>
                        )}
                    </>

                )}

                {(!stopped.isStopped) ? (
                    <Button
                        label={l('STOP_THE_UPDATE')}
                        onClick={handleStopUpdate}
                        className={classes.button}
                        alert
                    />
                ) : (
                    <Button
                        label={l('DONE')}
                        onClick={onDoneClick}
                        className={classes.button}
                    />
                )}
            </div>

            <HistoryBlockPrompt
                dialogTitle={l('UPLOAD_STILL_IN_PROGRESS')}
                promptText={l('YOU_ARE_ABOUT_TO_LEAVE_UPLOAD_PROCESS_WILL_STOP')}
                positiveLabel={l('DISCARD')}
                when={isLoading}
                action={handleStopUpdate}
                isPositiveAlert
            />
        </>
    )
}

export default UploadFeedback
